/**
 * Semantic Verification Engine
 * 
 * WHAT NOBODY ELSE IS DOING:
 * - Verifies AI suggestions against ACTUAL codebase state
 * - Cross-references imports with real node_modules and local files
 * - Validates function signatures against real definitions
 * - Checks type compatibility with actual TypeScript types
 */

import * as fs from 'fs';
import * as path from 'path';
import {
  CodeSuggestion,
  VerificationResult,
  VerificationIssue,
  VerificationMetrics,
  HallucinationType,
  CodebaseKnowledge,
  ImportInfo,
  AHFunctionInfo,
  AHParameterInfo,
} from './types';

export class SemanticVerifier {
  private workspaceRoot: string;
  private knowledge: CodebaseKnowledge;
  private nodeModulesCache: Map<string, Set<string>> = new Map();

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
    this.knowledge = this.initializeKnowledge();
  }

  private initializeKnowledge(): CodebaseKnowledge {
    return {
      imports: new Map(),
      exports: new Map(),
      types: new Map(),
      functions: new Map(),
      patterns: [],
      envVars: new Set(),
      routes: [],
    };
  }

  /**
   * Main verification entry point
   */
  async verify(suggestion: CodeSuggestion): Promise<VerificationResult> {
    const issues: VerificationIssue[] = [];
    
    // Extract code elements for verification
    const imports = this.extractImports(suggestion.code);
    const functionCalls = this.extractFunctionCalls(suggestion.code);
    const typeReferences = this.extractTypeReferences(suggestion.code);
    const envReferences = this.extractEnvReferences(suggestion.code);

    // Verify each category
    const importIssues = await this.verifyImports(imports);
    const apiIssues = await this.verifyAPICalls(functionCalls);
    const typeIssues = await this.verifyTypes(typeReferences);
    const envIssues = await this.verifyEnvVars(envReferences);

    issues.push(...importIssues, ...apiIssues, ...typeIssues, ...envIssues);

    // Calculate metrics
    const metrics = this.calculateMetrics(
      imports.length,
      importIssues.length,
      functionCalls.length,
      apiIssues.length,
      typeReferences.length,
      typeIssues.length
    );

    // Generate suggestions for fixes
    const suggestions = this.generateFixSuggestions(issues);

    return {
      valid: issues.filter(i => i.severity === 'error').length === 0,
      confidence: metrics.overallConfidence,
      issues,
      suggestions,
      metrics,
    };
  }

  /**
   * Extract import statements from code
   */
  private extractImports(code: string): Array<{ source: string; specifiers: string[]; line: number }> {
    const imports: Array<{ source: string; specifiers: string[]; line: number }> = [];
    const lines = code.split('\n');

    // ES6 imports
    const importRegex = /import\s+(?:(\{[^}]+\})|(\*\s+as\s+\w+)|(\w+))(?:\s*,\s*(?:(\{[^}]+\})|(\w+)))?\s+from\s+['"]([^'"]+)['"]/g;
    
    lines.forEach((line, index) => {
      let match;
      const lineImportRegex = /import\s+(?:(\{[^}]+\})|(\*\s+as\s+\w+)|(\w+))(?:\s*,\s*(?:(\{[^}]+\})|(\w+)))?\s+from\s+['"]([^'"]+)['"]/;
      match = lineImportRegex.exec(line);
      
      if (match) {
        const specifiers: string[] = [];
        // Named imports {a, b, c}
        if (match[1]) {
          const named = match[1].replace(/[{}]/g, '').split(',').map(s => s.trim().split(' as ')[0]);
          specifiers.push(...named);
        }
        // Namespace import * as X
        if (match[2]) {
          specifiers.push(match[2].replace('* as ', '').trim());
        }
        // Default import
        if (match[3]) {
          specifiers.push(match[3]);
        }
        // Additional named imports after default
        if (match[4]) {
          const named = match[4].replace(/[{}]/g, '').split(',').map(s => s.trim().split(' as ')[0]);
          specifiers.push(...named);
        }
        
        imports.push({
          source: match[6],
          specifiers,
          line: index + 1,
        });
      }
    });

    // CommonJS requires
    const requireRegex = /(?:const|let|var)\s+(?:(\{[^}]+\})|(\w+))\s*=\s*require\s*\(\s*['"]([^'"]+)['"]\s*\)/;
    lines.forEach((line, index) => {
      const match = requireRegex.exec(line);
      if (match) {
        const specifiers: string[] = [];
        if (match[1]) {
          const named = match[1].replace(/[{}]/g, '').split(',').map(s => s.trim());
          specifiers.push(...named);
        }
        if (match[2]) {
          specifiers.push(match[2]);
        }
        imports.push({
          source: match[3],
          specifiers,
          line: index + 1,
        });
      }
    });

    return imports;
  }

  /**
   * Extract function calls from code
   */
  private extractFunctionCalls(code: string): Array<{ name: string; object?: string; args: number; line: number }> {
    const calls: Array<{ name: string; object?: string; args: number; line: number }> = [];
    const lines = code.split('\n');

    lines.forEach((line, index) => {
      // Method calls: object.method(args)
      const methodRegex = /(\w+)\.(\w+)\s*\(([^)]*)\)/g;
      let match;
      while ((match = methodRegex.exec(line)) !== null) {
        const args = match[3].trim() ? match[3].split(',').length : 0;
        calls.push({
          object: match[1],
          name: match[2],
          args,
          line: index + 1,
        });
      }

      // Standalone function calls: func(args)
      const funcRegex = /(?<![.\w])(\w+)\s*\(([^)]*)\)/g;
      while ((match = funcRegex.exec(line)) !== null) {
        // Skip if it's a method call (has . before it)
        const beforeMatch = line.substring(0, match.index);
        if (!beforeMatch.endsWith('.') && !['if', 'for', 'while', 'switch', 'function', 'catch'].includes(match[1])) {
          const args = match[2].trim() ? match[2].split(',').length : 0;
          calls.push({
            name: match[1],
            args,
            line: index + 1,
          });
        }
      }
    });

    return calls;
  }

  /**
   * Extract type references from code
   */
  private extractTypeReferences(code: string): Array<{ name: string; line: number }> {
    const types: Array<{ name: string; line: number }> = [];
    const lines = code.split('\n');

    lines.forEach((line, index) => {
      // Type annotations: : Type, : Type[], : Type<T>
      const typeAnnotationRegex = /:\s*(\w+)(?:<[^>]+>)?(?:\[\])?/g;
      let match;
      while ((match = typeAnnotationRegex.exec(line)) !== null) {
        const typeName = match[1];
        // Skip primitive types
        if (!['string', 'number', 'boolean', 'void', 'any', 'unknown', 'never', 'null', 'undefined', 'object'].includes(typeName)) {
          types.push({ name: typeName, line: index + 1 });
        }
      }

      // Type assertions: as Type
      const assertionRegex = /\bas\s+(\w+)/g;
      while ((match = assertionRegex.exec(line)) !== null) {
        types.push({ name: match[1], line: index + 1 });
      }

      // Generic type parameters: <Type>
      const genericRegex = /<(\w+)(?:,\s*(\w+))*>/g;
      while ((match = genericRegex.exec(line)) !== null) {
        if (match[1] && !['T', 'K', 'V', 'U', 'P'].includes(match[1])) {
          types.push({ name: match[1], line: index + 1 });
        }
      }
    });

    return types;
  }

  /**
   * Extract environment variable references
   */
  private extractEnvReferences(code: string): Array<{ name: string; line: number }> {
    const envVars: Array<{ name: string; line: number }> = [];
    const lines = code.split('\n');

    lines.forEach((line, index) => {
      // process.env.VAR_NAME
      const processEnvRegex = /process\.env\.(\w+)/g;
      let match;
      while ((match = processEnvRegex.exec(line)) !== null) {
        envVars.push({ name: match[1], line: index + 1 });
      }

      // process.env['VAR_NAME'] or process.env["VAR_NAME"]
      const bracketEnvRegex = /process\.env\[['"](\w+)['"]\]/g;
      while ((match = bracketEnvRegex.exec(line)) !== null) {
        envVars.push({ name: match[1], line: index + 1 });
      }

      // import.meta.env.VAR_NAME (Vite)
      const metaEnvRegex = /import\.meta\.env\.(\w+)/g;
      while ((match = metaEnvRegex.exec(line)) !== null) {
        envVars.push({ name: match[1], line: index + 1 });
      }
    });

    return envVars;
  }

  /**
   * Verify imports against actual node_modules and local files
   */
  private async verifyImports(imports: Array<{ source: string; specifiers: string[]; line: number }>): Promise<VerificationIssue[]> {
    const issues: VerificationIssue[] = [];

    for (const imp of imports) {
      const { source, specifiers, line } = imp;

      // Check if it's a local import
      if (source.startsWith('.') || source.startsWith('/')) {
        const resolved = await this.resolveLocalImport(source);
        if (!resolved.exists) {
          issues.push({
            type: 'fake_import',
            severity: 'error',
            message: `Import '${source}' does not exist`,
            line,
            evidence: `File not found: ${resolved.attemptedPaths.join(', ')}`,
            fix: `Check if the file exists or correct the import path`,
          });
        } else {
          // Verify exported specifiers
          for (const specifier of specifiers) {
            if (!resolved.exports.includes(specifier) && specifier !== 'default') {
              issues.push({
                type: 'invented_api',
                severity: 'error',
                message: `'${specifier}' is not exported from '${source}'`,
                line,
                evidence: `Available exports: ${resolved.exports.slice(0, 5).join(', ')}${resolved.exports.length > 5 ? '...' : ''}`,
                fix: `Use one of the available exports or add '${specifier}' to the source file`,
              });
            }
          }
        }
      } else {
        // Node module import
        const moduleExists = await this.checkNodeModule(source);
        if (!moduleExists) {
          issues.push({
            type: 'fake_import',
            severity: 'error',
            message: `Package '${source}' is not installed`,
            line,
            evidence: `Not found in node_modules`,
            fix: `Run: npm install ${source}`,
          });
        } else {
          // Verify module exports (for popular packages)
          const invalidSpecifiers = await this.verifyModuleExports(source, specifiers);
          for (const invalid of invalidSpecifiers) {
            issues.push({
              type: 'invented_api',
              severity: 'warning',
              message: `'${invalid}' may not be exported from '${source}'`,
              line,
              fix: `Check the package documentation for correct exports`,
            });
          }
        }
      }
    }

    return issues;
  }

  /**
   * Verify API/function calls against known signatures
   */
  private async verifyAPICalls(calls: Array<{ name: string; object?: string; args: number; line: number }>): Promise<VerificationIssue[]> {
    const issues: VerificationIssue[] = [];

    for (const call of calls) {
      // Check against known function signatures
      const funcInfo = this.knowledge.functions.get(call.object ? `${call.object}.${call.name}` : call.name);
      
      if (funcInfo) {
        const requiredParams = funcInfo.parameters.filter((p: AHParameterInfo) => !p.optional).length;
        const maxParams = funcInfo.parameters.length;

        if (call.args < requiredParams) {
          issues.push({
            type: 'wrong_signature',
            severity: 'error',
            message: `'${call.name}' requires at least ${requiredParams} arguments, but got ${call.args}`,
            line: call.line,
            evidence: `Expected: ${funcInfo.parameters.map((p: AHParameterInfo) => `${p.name}${p.optional ? '?' : ''}: ${p.type}`).join(', ')}`,
            fix: `Add the required arguments`,
          });
        } else if (call.args > maxParams && !funcInfo.parameters.some((p: AHParameterInfo) => p.type.includes('...'))) {
          issues.push({
            type: 'wrong_signature',
            severity: 'warning',
            message: `'${call.name}' accepts at most ${maxParams} arguments, but got ${call.args}`,
            line: call.line,
          });
        }
      }

      // Check for common API mistakes
      const apiMistake = this.checkCommonAPIMistakes(call);
      if (apiMistake) {
        issues.push(apiMistake);
      }
    }

    return issues;
  }

  /**
   * Verify type references against defined types
   */
  private async verifyTypes(types: Array<{ name: string; line: number }>): Promise<VerificationIssue[]> {
    const issues: VerificationIssue[] = [];

    for (const type of types) {
      const typeInfo = this.knowledge.types.get(type.name);
      
      if (!typeInfo) {
        // Check if it's a well-known global type
        const wellKnownTypes = ['Promise', 'Array', 'Map', 'Set', 'Record', 'Partial', 'Required', 'Pick', 'Omit', 'Exclude', 'Extract', 'ReturnType', 'Parameters', 'InstanceType', 'Readonly', 'NonNullable', 'HTMLElement', 'Event', 'Error', 'Date', 'RegExp', 'JSON', 'Math', 'console', 'window', 'document', 'Response', 'Request', 'Headers', 'URL', 'URLSearchParams', 'FormData', 'Blob', 'File', 'FileReader', 'AbortController', 'AbortSignal', 'WebSocket', 'MessageEvent', 'EventSource', 'Worker', 'SharedWorker', 'ServiceWorker', 'Navigator', 'Storage', 'localStorage', 'sessionStorage', 'crypto', 'performance', 'fetch', 'XMLHttpRequest'];
        
        if (!wellKnownTypes.includes(type.name)) {
          issues.push({
            type: 'type_mismatch',
            severity: 'warning',
            message: `Type '${type.name}' is not defined in the codebase`,
            line: type.line,
            fix: `Define the type or import it from the correct module`,
          });
        }
      }
    }

    return issues;
  }

  /**
   * Verify environment variable references
   */
  private async verifyEnvVars(envVars: Array<{ name: string; line: number }>): Promise<VerificationIssue[]> {
    const issues: VerificationIssue[] = [];
    const knownEnvVars = await this.loadKnownEnvVars();

    for (const env of envVars) {
      if (!knownEnvVars.has(env.name)) {
        // Check for common env var patterns
        const isStandardVar = ['NODE_ENV', 'PORT', 'HOST', 'DEBUG', 'LOG_LEVEL'].includes(env.name);
        
        issues.push({
          type: 'env_hallucination',
          severity: isStandardVar ? 'info' : 'warning',
          message: `Environment variable '${env.name}' is not defined in .env files`,
          line: env.line,
          evidence: `Known env vars: ${Array.from(knownEnvVars).slice(0, 5).join(', ')}`,
          fix: `Add ${env.name} to your .env file`,
        });
      }
    }

    return issues;
  }

  /**
   * Resolve local import path
   */
  private async resolveLocalImport(source: string): Promise<{ exists: boolean; exports: string[]; attemptedPaths: string[] }> {
    const extensions = ['.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs', ''];
    const attemptedPaths: string[] = [];

    for (const ext of extensions) {
      const filePath = path.resolve(this.workspaceRoot, source + ext);
      attemptedPaths.push(filePath);
      
      if (fs.existsSync(filePath)) {
        const exports = await this.extractExportsFromFile(filePath);
        return { exists: true, exports, attemptedPaths };
      }

      // Try index file
      const indexPath = path.resolve(this.workspaceRoot, source, `index${ext}`);
      attemptedPaths.push(indexPath);
      
      if (fs.existsSync(indexPath)) {
        const exports = await this.extractExportsFromFile(indexPath);
        return { exists: true, exports, attemptedPaths };
      }
    }

    return { exists: false, exports: [], attemptedPaths };
  }

  /**
   * Extract exports from a file
   */
  private async extractExportsFromFile(filePath: string): Promise<string[]> {
    try {
      const content = fs.readFileSync(filePath, 'utf-8');
      const exports: string[] = [];

      // export const/let/var/function/class name
      const namedExportRegex = /export\s+(?:const|let|var|function|class|interface|type|enum)\s+(\w+)/g;
      let match;
      while ((match = namedExportRegex.exec(content)) !== null) {
        exports.push(match[1]);
      }

      // export { name1, name2 }
      const bracketExportRegex = /export\s*\{([^}]+)\}/g;
      while ((match = bracketExportRegex.exec(content)) !== null) {
        const names = match[1].split(',').map(s => s.trim().split(' as ')[0].trim());
        exports.push(...names);
      }

      // export default
      if (/export\s+default/.test(content)) {
        exports.push('default');
      }

      return exports;
    } catch {
      return [];
    }
  }

  /**
   * Check if a node module exists
   */
  private async checkNodeModule(moduleName: string): Promise<boolean> {
    // Handle scoped packages
    const packageName = moduleName.startsWith('@') 
      ? moduleName.split('/').slice(0, 2).join('/')
      : moduleName.split('/')[0];

    const nodeModulesPath = path.join(this.workspaceRoot, 'node_modules', packageName);
    return fs.existsSync(nodeModulesPath);
  }

  /**
   * Verify module exports (basic check for popular packages)
   */
  private async verifyModuleExports(moduleName: string, specifiers: string[]): Promise<string[]> {
    // Common package exports mapping (extensible)
    const knownExports: Record<string, string[]> = {
      'react': ['useState', 'useEffect', 'useCallback', 'useMemo', 'useRef', 'useContext', 'useReducer', 'createContext', 'forwardRef', 'memo', 'lazy', 'Suspense', 'Fragment', 'createElement', 'cloneElement', 'Children', 'Component', 'PureComponent', 'StrictMode'],
      'react-dom': ['render', 'hydrate', 'createPortal', 'findDOMNode', 'unmountComponentAtNode', 'createRoot', 'hydrateRoot'],
      'express': ['Router', 'json', 'urlencoded', 'static', 'raw', 'text'],
      'lodash': ['map', 'filter', 'reduce', 'find', 'findIndex', 'sortBy', 'groupBy', 'debounce', 'throttle', 'cloneDeep', 'merge', 'omit', 'pick', 'get', 'set', 'isEmpty', 'isEqual', 'uniq', 'flatten', 'chunk'],
      'axios': ['get', 'post', 'put', 'patch', 'delete', 'request', 'create', 'isAxiosError', 'all', 'spread'],
      'zod': ['z', 'string', 'number', 'boolean', 'object', 'array', 'enum', 'union', 'intersection', 'literal', 'optional', 'nullable', 'infer'],
    };

    const exports = knownExports[moduleName];
    if (!exports) return []; // Can't verify unknown packages

    return specifiers.filter(s => s !== 'default' && !exports.includes(s));
  }

  /**
   * Check for common API mistakes
   */
  private checkCommonAPIMistakes(call: { name: string; object?: string; args: number; line: number }): VerificationIssue | null {
    const mistakes: Record<string, { correct: string; message: string }> = {
      'array.length()': { correct: 'array.length', message: 'length is a property, not a method' },
      'string.length()': { correct: 'string.length', message: 'length is a property, not a method' },
      'console.write': { correct: 'console.log', message: 'Use console.log instead of console.write' },
      'array.contains': { correct: 'array.includes', message: 'Use includes() instead of contains()' },
      'string.contains': { correct: 'string.includes', message: 'Use includes() instead of contains()' },
      'object.hasKey': { correct: 'Object.hasOwn or "key" in object', message: 'Use Object.hasOwn() or the in operator' },
    };

    const key = call.object ? `${call.object}.${call.name}` : call.name;
    const mistake = mistakes[key];

    if (mistake) {
      return {
        type: 'invented_api',
        severity: 'error',
        message: mistake.message,
        line: call.line,
        fix: `Use ${mistake.correct} instead`,
      };
    }

    return null;
  }

  /**
   * Load known environment variables from .env files
   */
  private async loadKnownEnvVars(): Promise<Set<string>> {
    const envFiles = ['.env', '.env.local', '.env.example', '.env.development', '.env.production'];
    const envVars = new Set<string>();

    for (const file of envFiles) {
      const filePath = path.join(this.workspaceRoot, file);
      if (fs.existsSync(filePath)) {
        const content = fs.readFileSync(filePath, 'utf-8');
        const lines = content.split('\n');
        
        for (const line of lines) {
          const match = /^([A-Z_][A-Z0-9_]*)\s*=/.exec(line);
          if (match) {
            envVars.add(match[1]);
          }
        }
      }
    }

    return envVars;
  }

  /**
   * Calculate verification metrics
   */
  private calculateMetrics(
    totalImports: number,
    importIssues: number,
    totalCalls: number,
    callIssues: number,
    totalTypes: number,
    typeIssues: number
  ): VerificationMetrics {
    const importVerification = totalImports > 0 ? ((totalImports - importIssues) / totalImports) * 100 : 100;
    const apiVerification = totalCalls > 0 ? ((totalCalls - callIssues) / totalCalls) * 100 : 100;
    const typeVerification = totalTypes > 0 ? ((totalTypes - typeIssues) / totalTypes) * 100 : 100;
    
    // Pattern alignment would require deeper analysis
    const patternAlignment = 85; // Placeholder, will be calculated by PatternLearner

    const overallConfidence = (importVerification * 0.35 + apiVerification * 0.35 + typeVerification * 0.2 + patternAlignment * 0.1);

    return {
      importVerification: Math.round(importVerification),
      apiVerification: Math.round(apiVerification),
      typeVerification: Math.round(typeVerification),
      patternAlignment,
      overallConfidence: Math.round(overallConfidence),
    };
  }

  /**
   * Generate fix suggestions based on issues
   */
  private generateFixSuggestions(issues: VerificationIssue[]): string[] {
    const suggestions: string[] = [];

    const fakeImports = issues.filter(i => i.type === 'fake_import');
    if (fakeImports.length > 0) {
      suggestions.push(`Install missing packages: npm install ${fakeImports.map(i => i.message.match(/Package '([^']+)'/)?.[1]).filter(Boolean).join(' ')}`);
    }

    const inventedAPIs = issues.filter(i => i.type === 'invented_api');
    if (inventedAPIs.length > 0) {
      suggestions.push(`Check API documentation for correct method names and exports`);
    }

    const envIssues = issues.filter(i => i.type === 'env_hallucination');
    if (envIssues.length > 0) {
      suggestions.push(`Add missing env vars to .env: ${envIssues.map(i => i.message.match(/variable '([^']+)'/)?.[1]).filter(Boolean).join(', ')}`);
    }

    return suggestions;
  }

  /**
   * Update knowledge base with new information
   */
  updateKnowledge(knowledge: Partial<CodebaseKnowledge>): void {
    if (knowledge.imports) {
      knowledge.imports.forEach((v, k) => this.knowledge.imports.set(k, v));
    }
    if (knowledge.exports) {
      knowledge.exports.forEach((v, k) => this.knowledge.exports.set(k, v));
    }
    if (knowledge.types) {
      knowledge.types.forEach((v, k) => this.knowledge.types.set(k, v));
    }
    if (knowledge.functions) {
      knowledge.functions.forEach((v, k) => this.knowledge.functions.set(k, v));
    }
    if (knowledge.patterns) {
      this.knowledge.patterns.push(...knowledge.patterns);
    }
    if (knowledge.envVars) {
      knowledge.envVars.forEach(v => this.knowledge.envVars.add(v));
    }
    if (knowledge.routes) {
      this.knowledge.routes.push(...knowledge.routes);
    }
  }
}
